import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AuthService } from '@klickdata/core/auth';
import { File } from '@klickdata/core/file';
import { User, UserService } from '@klickdata/core/user';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-media-file-shared-with',
    templateUrl: './add-media-file-shared-with.component.html',
    styleUrls: ['./add-media-file-shared-with.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class AddMediaFileSharedWithComponent extends OnDestroyHandler implements AfterViewInit, OnInit {
    @Input() public file: File;
    @ViewChild('userRecieverInput') public userRecieverInput: ElementRef<HTMLInputElement>;
    public parentForm: FormGroup;
    public userCtrl = new FormControl();
    public userSearch$: Observable<User[]>;
    public userRecipients: User[] = [];
    public authUser: User;

    constructor(
        @Optional() protected parentFormDirective: FormGroupDirective,
        protected userService: UserService,
        protected changeRef: ChangeDetectorRef,
        protected authService: AuthService
    ) {
        super();
        this.authService
            .getUser()
            .pipe(take(1), takeUntil(this.destroy))
            .subscribe((user) => {
                this.authUser = user;
                this.changeRef.markForCheck();
            });
    }

    ngOnInit(): void {
        this.parentForm = this.parentFormDirective?.form;
        if (this.parentForm) {
            this.ensureManagerControlExists();
        }

        const managerIds = this.parentForm?.get('staff.manager')?.value;

        if (Array.isArray(managerIds) && managerIds.length > 0) {
            this.userService
                .getUsers(managerIds)
                .pipe(take(1), takeUntil(this.destroy))
                .subscribe((users) => {
                    this.userRecipients = [...users];
                    this.updateRecipientIds();
                    this.changeRef.markForCheck();
                });
        }
    }

    ngAfterViewInit(): void {
        if (!this.authUser) {
            return;
        }

        this.userSearch$ = this.userCtrl.valueChanges
            .pipe(
                filter((term: string | User) => typeof term === 'string' && term.trim() !== ''),
                debounceTime(300),
                distinctUntilChanged()
            )
            .pipe(
                switchMap((term) =>
                    typeof term === 'string' && term.trim()
                        ? this.userService.getCustomerUsers({ customerId: this.authUser.customer_id, term: term })
                        : of(<User[]>[])
                ),
                map((users) =>
                    users.filter(
                        (user) =>
                            this.userRecipients.findIndex((recipient) => recipient.id === user.id) === -1 &&
                            user.id !== this.authUser.id
                    )
                ),
                takeUntil(this.destroy)
            );
    }

    public selected(auto: MatAutocomplete, event: MatAutocompleteSelectedEvent): void {
        this.userRecipients.push(event.option.value);

        if (this.userRecieverInput) {
            this.userRecieverInput.nativeElement.value = '';
        }

        this.userCtrl.setValue('');
        auto.options.reset([]);

        this.updateRecipientIds(true);
        this.changeRef.markForCheck();
    }

    public removeUser(user: User): void {
        const index = this.userRecipients.indexOf(user);

        if (index >= 0) {
            this.userRecipients.splice(index, 1);
            this.updateRecipientIds(true);
            this.changeRef.markForCheck();
        }
    }

    private ensureManagerControlExists(): void {
        this.parentForm.addControl(
            'staff',
            new FormGroup({
                manager: new FormControl([]),
            })
        );
    }

    private updateRecipientIds(WithEmitEvent: boolean = false): void {
        if (!this.parentForm) {
            return;
        }

        const control = this.parentForm.get('staff.manager');

        if (control && control instanceof FormControl) {
            const managerIds = this.userRecipients.map((user) => user.id);
            control.setValue(managerIds, { emitEvent: WithEmitEvent });
            if (WithEmitEvent) {
                control.markAsDirty();
            }
        }
    }
}
